import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { useGetUserSession } from './auth'
import { LLM_CHAT_SESSION_QUERY_ROOT } from './llmChat'

export const CURRENT_ORGANIZATION_QUERY_KEY = 'currentOrganization'

/**
 * Poll interval so `Root` re-runs trial / access checks without navigation or window blur
 * (organization holds `trialEndsAt` and `accessPlan`, not the session query).
 */
const CURRENT_ORGANIZATION_REFETCH_INTERVAL_MS = 30 * 60 * 1000

/** Keep aligned with server `app/constants/organizationAccessPlan.ts` string values. */
export const ORGANIZATION_ACCESS_PLANS = ['FREE_TRIAL', 'FULL_ACCESS'] as const

export type OrganizationAccessPlan = (typeof ORGANIZATION_ACCESS_PLANS)[number]

/** Matches Prisma `LlmGatewayProvider` for org-scoped Lex LLM chat. */
export const ORGANIZATION_LLM_GATEWAY_PROVIDERS = ['openai', 'qwen'] as const

export type OrganizationLlmGatewayProvider =
  (typeof ORGANIZATION_LLM_GATEWAY_PROVIDERS)[number]

export type Organization = {
  id: string
  name: string
  localName: string | null
  street: string | null
  street2: string | null
  streetNumber: string | null
  subDivision: string | null
  country: string | null
  postCode: string | null
  poBox: string | null
  city: string | null
  contactName: string | null
  contactPhoneNumber: string | null
  contactEmail: string | null
  trialTermsAcceptedAt: string | null
  trialEndsAt: string | null
  trialTermsAcceptedByUserId: string | null
  contactSalesRequestedAt: string | null
  accessPlan: OrganizationAccessPlan
  onboardingCompletedAt: string | null
  llmGatewayProvider: OrganizationLlmGatewayProvider
  packagingSuppliersEnabled: boolean
}

export type UpdateOrganizationPayload = {
  name: string
  localName: string | null
  street: string | null
  street2: string | null
  streetNumber: string | null
  subDivision: string | null
  country: string | null
  postCode: string | null
  poBox: string | null
  city: string | null
  contactName: string | null
  contactPhoneNumber: string | null
  contactEmail: string | null
  llmGatewayProvider?: OrganizationLlmGatewayProvider
  packagingSuppliersEnabled?: boolean
}

export function useCurrentOrganization() {
  const { data: session } = useGetUserSession()

  return useQuery<Organization>({
    queryKey: [CURRENT_ORGANIZATION_QUERY_KEY, session?.organizationId],
    queryFn: async () => {
      const organizationId = session?.organizationId
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }
      const response = await apiClient.get<Organization>(
        `/organizations/${organizationId}`
      )
      return response.data
    },
    enabled: !!session?.organizationId,
    refetchInterval: CURRENT_ORGANIZATION_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      organizationId,
      payload,
    }: {
      organizationId: string
      payload: Partial<UpdateOrganizationPayload>
    }) => {
      await apiClient.patch(`/organizations/${organizationId}`, payload)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [CURRENT_ORGANIZATION_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [LLM_CHAT_SESSION_QUERY_ROOT, 'providerInfo'],
      })
    },
  })
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (organizationId: string) => {
      const response = await apiClient.post<Organization>(
        `/organizations/${organizationId}/onboarding/complete`,
        { acceptedTerms: true }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [CURRENT_ORGANIZATION_QUERY_KEY],
      })
    },
  })
}

export function useRecordContactSalesInterest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (organizationId: string) => {
      await apiClient.post(`/organizations/${organizationId}/contact-sales`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [CURRENT_ORGANIZATION_QUERY_KEY],
      })
    },
  })
}
